"use client"

import { Head } from "@inertiajs/react"
import { MessageChatCircle, PlayCircle, Zap } from "@untitledui/icons"
import { type ComponentProps, type FC, type ReactNode } from "react"

import { Button } from "@uui/base/buttons/button"
import { FeaturedIcon } from "@uui/foundations/featured-icon/featured-icon"
import { DropdownMenuFeatureCard } from "@uui/marketing/header-navigation/dropdown-menu-feature-card"
import { DropdownMenuSimpleWithFooter } from "@uui/marketing/header-navigation/dropdown-menu-simple-with-footer"
import { DropdownMenuWithTwoColsAndLinksAndFooter } from "@uui/marketing/header-navigation/dropdown-menu-with-two-cols-and-links-and-footer"
import { Header } from "@uui/marketing/header-navigation/header"
import { SectionDivider } from "@uui/shared-assets/section-divider"
import { cx } from "@uui/utils/cx"

const BatyrLogo = ({
  className,
  variant = "dark",
}: {
  className?: string
  variant?: "dark" | "light"
}) => (
  <img
    src={variant === "light" ? "/icon-light.svg" : "/icon.svg"}
    alt="Batyr Logo"
    className={className}
  />
)

const HeaderPrimaryDark = (props: ComponentProps<typeof Header>) => {
  // Generic placeholder nav for project scaffolding. All simple links point to "#".
  // Keep dropdowns where they exist to illustrate structure, but with placeholder labels.
  const items = [
    { label: "Lorem", menu: <DropdownMenuSimpleWithFooter /> },
    { label: "Ipsum", menu: <DropdownMenuFeatureCard /> },
    { label: "Dolor", href: "#" },
    { label: "Sit Amet", menu: <DropdownMenuWithTwoColsAndLinksAndFooter /> },
    { label: "Consectetur", href: "#" },
  ] as const
  return (
    <Header
      {...props}
      items={items as unknown as ComponentProps<typeof Header>["items"]}
      logo={<BatyrLogo className="h-8 w-auto" variant="light" />}
      className={cx(
        "bg-brand-section [&_nav>ul>li>a]:text-secondary_on-brand [&_nav>ul>li>a]:hover:text-secondary_on-brand [&_nav>ul>li>button]:text-secondary_on-brand [&_nav>ul>li>button]:hover:text-secondary_on-brand [&_nav>ul>li>button>svg]:text-fg-brand-secondary_alt [&_svg_path.fill-fg-primary]:fill-fg-white",
        props.className,
      )}
    />
  )
}

const HeroSimpleText02 = () => {
  return (
    <div className="bg-brand-section relative overflow-hidden">
      {/* Background pattern */}
      <img
        alt="Grid of dots"
        aria-hidden="true"
        loading="lazy"
        src="https://www.untitledui.com/patterns/light/grid-dot-sm-desktop.svg"
        className="pointer-events-none absolute top-0 left-1/2 z-0 hidden max-w-none -translate-x-1/2 opacity-20 md:block"
      />
      <img
        alt="Grid of dots"
        aria-hidden="true"
        loading="lazy"
        src="https://www.untitledui.com/patterns/light/grid-dot-sm-mobile.svg"
        className="pointer-events-none absolute top-0 left-1/2 z-0 max-w-none -translate-x-1/2 opacity-20 md:hidden"
      />

      <HeaderPrimaryDark />

      <section className="relative py-16 md:py-24">
        <div className="max-w-container mx-auto w-full px-4 md:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-8">
            <div className="flex flex-col">
              <h1 className="text-display-md text-primary_on-brand md:text-display-lg lg:text-display-xl font-medium">
                A New Batyr App
              </h1>
              <p className="text-tertiary_on-brand mt-4 max-w-(--breakpoint-sm) text-lg md:mt-6 md:text-xl">
                Batyr is a for-purpose preventative mental health organisation,
                created by young people, for young people.
              </p>
              <div className="mt-8 flex w-full flex-col-reverse items-stretch gap-3 sm:w-auto sm:flex-row sm:items-start md:mt-12">
                <Button size="xl" color="primary">
                  Try it out
                </Button>
                <Button
                  iconLeading={PlayCircle}
                  color="secondary"
                  size="xl"
                  className="border-white bg-transparent text-white shadow-xs! ring-0 hover:bg-white/10"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="w-full">
              <img
                alt="Two Supporters looking at phone"
                src="https://cdn.prod.website-files.com/608b63a78e49d2b3debdb720/630ea690d0f10e4e28be0997_Group%2013%402x.png"
                className="h-60 w-full rounded-2xl object-cover shadow-xl lg:h-129"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

const SocialProofFullWidthBrand = () => {
  return (
    <section className="bg-brand-section pb-16 md:pb-24">
      <div className="max-w-container mx-auto px-4 md:px-8">
        <div className="flex flex-col gap-8">
          <p className="text-md text-tertiary_on-brand text-center font-medium">
            Proudly partnering with major organisations
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-xl font-semibold text-white/80 xl:gap-x-12">
            <span>Zurich</span>
            <span>The Awesome Foundation</span>
            <span>Hunt & Brew</span>
            <span>ANU</span>
            <span>USyd</span>
          </div>
        </div>
      </div>
    </section>
  )
}

interface TextCentered {
  title: string
  subtitle: string
  footer?: ReactNode
}

interface FeatureTextIcon extends TextCentered {
  icon: FC<{ className?: string }>
}

const FeatureTextFeaturedIconLeft = ({
  icon,
  title,
  subtitle,
  footer,
}: FeatureTextIcon) => (
  <div className="flex max-w-140 gap-4">
    <FeaturedIcon
      icon={icon}
      size="lg"
      color="brand"
      theme="modern"
      className="hidden md:inline-flex"
    />
    <FeaturedIcon
      icon={icon}
      size="md"
      color="brand"
      theme="modern"
      className="inline-flex md:hidden"
    />

    <div className="flex flex-col items-start gap-4">
      <div>
        <h3 className="text-primary mt-1.5 text-lg font-semibold md:mt-2.5">
          {title}
        </h3>
        <p className="text-md text-tertiary mt-1">{subtitle}</p>
      </div>

      {footer}
    </div>
  </div>
)

const FeaturesIconsAndImage04 = () => {
  return (
    <section className="bg-primary">
      <div className="bg-secondary pt-16 pb-[112px] md:pt-24 md:pb-40">
        <div className="max-w-container mx-auto grid w-full grid-cols-1 gap-12 px-4 md:gap-16 md:px-8 lg:grid-cols-2 lg:gap-24">
          <div className="flex w-full flex-col">
            <span className="text-brand-tertiary-alt md:text-md text-sm font-semibold">
              Who we are
            </span>

            <h2 className="text-display-sm text-primary md:text-display-md mt-3 font-semibold">
              Giving a voice to the elephant in the room.
            </h2>
            <p className="text-tertiary mt-4 text-lg md:mt-5 md:text-xl">
              We engage, educate and empower young people to have positive
              conversations about mental health.
            </p>
          </div>

          <ul className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-1">
            {[
              {
                title: "Sharing Stories",
                subtitle:
                  "Empowering young people to share their lived experiences to smash stigma.",
                icon: MessageChatCircle,
              },
              {
                title: "Educational Programs",
                subtitle:
                  "Delivering high-energy programs in schools and universities across Australia.",
                icon: Zap,
              },
            ].map((item) => (
              <li key={item.title}>
                <FeatureTextFeaturedIconLeft
                  icon={item.icon}
                  title={item.title}
                  subtitle={item.subtitle}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="max-w-container mx-auto -mt-16 flex w-full justify-center px-4 pb-16 md:-mt-24 md:px-8 md:pb-24">
        <img
          src="https://byrbatyr.blob.core.windows.net/assets/nova/1_2.png"
          alt="Street Sign Campaign"
          className="h-60 w-full rounded-2xl object-cover shadow-xl md:h-100 lg:h-129"
        />
      </div>
    </section>
  )
}

const footerLinks = [
  { label: "About Us", href: "https://www.batyr.com.au/our-story" },
  { label: "batyr@school", href: "https://www.batyr.com.au/batyr-school" },
  { label: "batyr@uni", href: "https://www.batyr.com.au/batyr-uni" },
  { label: "Donate", href: "https://www.batyr.com.au/donate" },
  { label: "Get Support", href: "https://www.batyr.com.au/get-support" },
]

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/batyr-pty-ltd/posts/?feedView=all",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCJ3OYd9DwLA5r4QHvzAxxKQ",
  },
  { label: "TikTok", href: "https://www.tiktok.com/@batyraus" },
  { label: "Instagram", href: "https://www.instagram.com/ourherd.io" },
  { label: "Facebook", href: "https://www.facebook.com/batyraus/" },
]

const legalLinks = [
  { label: "Terms", href: "#" },
  { label: "Privacy", href: "#" },
  { label: "Cookies", href: "#" },
]

const Footer = () => {
  return (
    <footer className="bg-primary py-12 md:py-16">
      <div className="max-w-container mx-auto px-4 md:px-8">
        {/* Main footer row: Quick links left, Socials right */}
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <nav>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {footerLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="text-tertiary hover:text-primary text-sm font-medium transition"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-6">
            <span className="text-quaternary text-sm">Follow us</span>
            <ul className="flex gap-4">
              {socialLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tertiary hover:text-primary text-sm transition"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom row: Copyright left, Legal links right */}
        <div className="border-secondary mt-10 flex flex-col-reverse justify-between gap-4 border-t pt-8 md:flex-row md:items-center">
          <p className="text-quaternary text-sm">
            © 2026 Batyr. All rights reserved.
          </p>

          <ul className="flex gap-6">
            {legalLinks.map(({ label, href }) => (
              <li key={label}>
                <a
                  href={href}
                  className="text-quaternary hover:text-tertiary text-sm transition"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  )
}

export default function Home() {
  return (
    <div className="bg-primary">
      <Head title="Home - Batyr" />
      <HeroSimpleText02 />
      <SocialProofFullWidthBrand />
      <FeaturesIconsAndImage04 />
      <SectionDivider />
      <Footer />
    </div>
  )
}
